import { useState, useEffect } from "react";
import { apiFetch } from "../../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const useAdminAccountCreation = () => {
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState({ type: "", text: "" });

    const [form, setForm] = useState({
        sr: "",
        email: "",
        clientName: "",
        infinityCode: "",
        phoneNumber: "",
        location: "",
        password: "",
    });

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: "", text: "" }), 5000);
    };

    const fetchCompanies = async () => {
        try {
            setLoadingCompanies(true);
            const response = await apiFetch(`${BASE_URL}/api/configurations/companies`, { method: "GET" });
            const data = await response.json();
            const list = data?.data || [];
            setCompanies(list);
            if (!form.clientName && list.length > 0) {
                setForm((prev) => ({ ...prev, clientName: list[0].clientName }));
            }
        } catch (error) {
            console.error("Failed to load companies:", error);
            showMessage("error", "Failed to load companies");
        } finally {
            setLoadingCompanies(false);
        }
    };

    useEffect(() => {
        fetchCompanies();
    }, []);

    const onChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const validate = () => {
        if (!form.sr.trim()) {
            showMessage("error", "Admin name is required");
            return false;
        }
        if (!form.email.trim()) {
            showMessage("error", "Email is required");
            return false;
        }
        if (!form.clientName.trim()) {
            showMessage("error", "Company is required");
            return false;
        }
        if (!form.password.trim()) {
            showMessage("error", "Password is required");
            return false;
        }
        if (form.password.trim().length < 4) {
            showMessage("error", "Password must be at least 4 characters");
            return false;
        }
        return true;
    };

    const handleCreateAdmin = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        try {
            setSaving(true);

            const payload = {
                sr: form.sr.trim(),
                email: form.email.trim(),
                clientName: form.clientName.trim(),
                employeeState: "Active",
                password: form.password,
                loginCount: 2,
            };

            if (form.infinityCode.trim()) payload.infinityCode = form.infinityCode.trim();
            if (form.phoneNumber.trim()) payload.phoneNumber = form.phoneNumber.trim();
            if (form.location.trim()) payload.location = form.location.trim();

            const createResponse = await apiFetch(`${BASE_URL}/employees`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!createResponse.ok) {
                const errorText = await createResponse.text();
                throw new Error(errorText || "Failed to create employee profile");
            }

            const createJson = await createResponse.json();
            const createdInfinityCode =
                createJson?.employee?.infinityCode || createJson?.data?.infinityCode || payload.infinityCode;

            if (!createdInfinityCode) {
                throw new Error("Employee created but Infinity Code is missing");
            }

            const promoteResponse = await apiFetch(`${BASE_URL}/employees/make-admin`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    infinityCodes: [createdInfinityCode],
                    clientName: form.clientName.trim(),
                }),
            });

            if (!promoteResponse.ok) {
                const errorText = await promoteResponse.text();
                throw new Error(errorText || "Employee created but failed to grant admin role");
            }

            showMessage("success", `Admin account created for ${form.email.trim()}`);
            setForm({
                sr: "",
                email: "",
                clientName: form.clientName,
                infinityCode: "",
                phoneNumber: "",
                location: "",
                password: "",
            });
        } catch (error) {
            console.error("Failed to create admin account:", error);
            showMessage("error", error.message || "Failed to create admin account");
        } finally {
            setSaving(false);
        }
    };

    return {
        companies,
        loadingCompanies,
        saving,
        message,
        form,
        onChange,
        handleCreateAdmin,
    };
};

export default useAdminAccountCreation;